import axios from 'axios';
import Environment from "../utils/Environment";

export interface ApiRouteResponse {
    id: number;
    dstCntrCd: string;
    routeId: string;
    fileNm: string;
    status: string;
    splitFlag: string | null;
    comboFlag: string | null;
    xdockFlag: string | null;
    replayFlag: string;
    replayCount: number;
    lastReplyUser: string | null;
    routePayload: string;
    remarks: string | null;
    createTs: string;
    createUserId: string;
    lastUpdTs: string;
    lastUpdUserId: string;
    isPastData: boolean;
}

export interface ApiResponse {
    content: ApiRouteResponse[];
    size: number;
    sort: string;
    totalPages: number;
    page: number;
    totalElements: number;
}

export interface RouteFetchRequest {
    dcName: string;
    fileName: string;
    routeId: string;
    startDate: string;
    endDate: string;
    status: string;
    page: number;
    size: number;
}

export interface SplitWorksheetData {
    id: string;
    store: string;
    routeGroupId: string | null;
    route: string;
    productTypeCd: string;
    cube: number;
    weight: number;
    createTs: string;
    cosmosOrderId: string;
}

export interface ComboRoutes {
    camsRouteSk: number;
    dstCntrCd: string;
    orderRouteId: string;
    camsRouteOrderSk: number;
    camsRouteStopSk: number;
    stop: string;
    sequence: string;
    weight: number;
    cube: number;
    arrivalTs: string;
    departureTs: string;
    productTypeCd: string | null;
    routeGroupId: string | null;
    splitIndicator?: boolean | string | number | null;
}

export interface RouteStop {
    StopNumber: string;
    ArrivalDateTime: string;
    DepartureDateTime: string;
    DestinationFacilityId: string;
}

export interface RouteOrder {
    OriginalOrderId: string;
    XrefOrderId: string;
    RouteId: string;
    OrderGroupId: string;
    RouteGroupId: string;
    DestinationFacilityId: string;
    PickupStopNumber: string;
    DeliveryStopNumber: string;
    ArrivalDateTime: string;
    DepartureDateTime: string;
    ReferenceId: string;
    ProductTypeId: string;
    Weight: string;
    Cube: string;
    QuantityType: string;
}

export interface RoutePayload {
    OrgId: string;
    FacilityId: string;
    RouteId: string;
    RouteGroupId: string;
    DispatchDateTime: string;
    RoutingScenarioId: string;
    EarliestDispatchDateTime: string;
    LatestDispatchDateTime: string;
    RouteStop: RouteStop[];
    RouteOrder: RouteOrder[];
    ResourceTypeId: string;
}

export interface RouteViewPayloadResponse {
    id: number;
    payload: any;
    dcName: string;
}

export interface RouteComboResponse {
    [key: string]: ComboRoutes[];
}

export interface RouteSplitRequest {
    dcName: string;
    store?: string;
    fromDate?: string;
    toDate?: string;
}

export const isIndicatorEnabled = (value: boolean | string | number | null | undefined): boolean => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (typeof value === 'number') {
        return value === 1;
    }

    const normalized = String(value || '').trim().toLowerCase();
    if (!normalized) {
        return false;
    }

    const disabledValues = new Set(['false', '0', 'no', 'off', 'n', 'null', 'undefined']);
    return !disabledValues.has(normalized);
};

export const isRouteFlagEnabled = (flag: string | null | undefined): boolean => {
    return isIndicatorEnabled(flag);
};

const fetchRoutes = async (request: RouteFetchRequest, signal?: AbortSignal): Promise<ApiResponse> => {
    const apiUrl = Environment.getRouteFetchAPI();
    try {
        const response = await axios.post(apiUrl, request, {
            headers: {
                'Content-Type': 'application/json',
            },
            signal,
        });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
    } catch (err: any) {
        console.error('Error fetching routes:', err);
        throw err;
    }
};

const fetchSplitRoutes = async (request: RouteSplitRequest, signal?: AbortSignal): Promise<SplitWorksheetData[]> => {
    const apiUrl = Environment.getRouteSplitAPI();
    try {
        const params = new URLSearchParams();
        params.append('dcName', request.dcName);
        if (request.store) {
            params.append('store', request.store);
        }
        if (request.fromDate) {
            params.append('fromDate', request.fromDate);
        }
        if (request.toDate) {
            params.append('toDate', request.toDate);
        }

        const response = await axios.get(`${apiUrl}?${params.toString()}`, {
            headers: {
                'Content-Type': 'application/json',
            },
            signal,
        });
        if (response.status === 200) {
            const transformedData = response.data.map((item: any) => ({
                id: [item.cosmosOrderId, item.store, item.route, item.createTs].filter(Boolean).join('-'),
                store: item.store,
                routeGroupId: item.routeGroupId ?? null,
                route: item.route,
                productTypeCd: item.productTypeCd || '',
                cube: item.cube,
                weight: item.weight,
                createTs: item.createTs,
                cosmosOrderId: item.cosmosOrderId || ''
            }));
            return transformedData;
        } else {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
    } catch (err: any) {
        console.error('Error fetching split routes:', err);
        throw err;
    }
};

const fetchComboRoutes = async (id: number, dcName: string, createTs: string, isSplit: boolean, routeId: string): Promise<RouteComboResponse> => {
    const apiUrl = Environment.getRouteComboAPI().replace('{id}', id.toString());
    try {
        const params = new URLSearchParams();
        params.append('dcName', dcName);
        params.append('createTs', createTs);
        params.append('isSplit', String(isSplit));
        if (routeId) {
            params.append('routeId', routeId);
        }

        const response = await axios.get(`${apiUrl}?${params.toString()}`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
    } catch (err: any) {
        console.error('Error fetching combo routes:', err);
        throw err;
    }
};

const fetchViewPayload = async (id: number, dcName: string): Promise<any> => {
    const apiUrl = Environment.getRouteViewPayloadAPI().replace('{id}', id.toString());
    try {
        const params = new URLSearchParams();
        params.append('dcName', dcName);

        const response = await axios.get<RouteViewPayloadResponse>(`${apiUrl}?${params.toString()}`, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.status === 200) {
            return response.data.payload.message;
        } else {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
    } catch (err: any) {
        console.error('Error fetching view payload:', err);
        throw err;
    }
};

const fetchReplay = async (id: number, dcName: string, user: string): Promise<any> => {
    const apiUrl = Environment.getRouteReplayAPI().replace('{id}', id.toString());
    try {
        const params = new URLSearchParams();
        params.append('dcName', dcName);
        params.append('user', user);

        const fullUrl = `${apiUrl}?${params.toString()}`;

        const response = await axios.get(fullUrl, {
            headers: {
                'Content-Type': 'application/json',
            },
        });
        if (response.status === 200) {
            return response.data;
        } else {
            throw new Error(`API request failed: ${response.status} ${response.statusText}`);
        }
    } catch (err: any) {
        console.error('Error fetching replay:', err);
        throw err;
    }
};

export { fetchSplitRoutes, fetchComboRoutes, fetchViewPayload, fetchReplay };
export default fetchRoutes;